import { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';

export const useNotifications = () => {
  const { user } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch notifications for the current user
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      console.log('Fetched notifications:', data);
      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.is_read).length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Mark a single notification as read
  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .update({ 
          is_read: true, 
          read_at: new Date().toISOString() 
        })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Create a new in-app notification
  const createNotification = async (userId, notificationData) => {
    try {
      const { error } = await supabase
        .from('user_notifications')
        .insert([{
          user_id: userId,
          ...notificationData
        }]);

      if (error) throw error;

      // If it's for the current user, refresh notifications
      if (userId === user?.id) {
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user?.id) return;

    // Initial fetch
    fetchNotifications();

    console.log('Setting up real-time subscription for user:', user.id);

    // Chrome-compatible subscription setup
    const subscription = supabase
      .channel(`user_notifications_${user.id}`, {
        config: {
          broadcast: { self: true },
          presence: { key: user.id }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'user_notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Real-time notification event:', payload);
          
          if (payload.eventType === 'INSERT') {
            console.log('New notification received:', payload.new);
            // Add new notification to the top of the list
            setNotifications(prev => {
              const newNotification = payload.new;
              // Avoid duplicates
              if (prev.some(n => n.id === newNotification.id)) {
                return prev;
              }
              return [newNotification, ...prev];
            });
            setUnreadCount(prev => prev + 1);
          } else if (payload.eventType === 'UPDATE') {
            console.log('Notification updated:', payload.new);
            // Update existing notification
            setNotifications(prev => 
              prev.map(n => n.id === payload.new.id ? payload.new : n)
            );
            // Recalculate unread count
            const wasRead = payload.old?.is_read;
            const isRead = payload.new?.is_read;
            if (!wasRead && isRead) {
              setUnreadCount(prev => Math.max(0, prev - 1));
            } else if (wasRead && !isRead) {
              setUnreadCount(prev => prev + 1);
            }
          }
        }
      )
      .subscribe((status, err) => {
        console.log('Subscription status:', status);
        if (err) {
          console.error('Subscription error:', err);
        }
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to notifications!');
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('Real-time subscription failed, falling back to polling');
        }
      });

    // Aggressive polling for Chrome compatibility 
    const pollInterval = setInterval(() => {
      console.log('Polling for notifications (Chrome compatibility)...');
      fetchNotifications();
    }, 5000); // Poll every 5 seconds for Chrome

    // Additional manual refresh trigger for when user becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Tab became visible, refreshing notifications...');
        fetchNotifications();
      }
    };

    // Custom event listener for immediate updates when notifications are created
    const handleNotificationCreated = () => {
      console.log('Custom notification event received - fetching updates');
      fetchNotifications();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('notificationCreated', handleNotificationCreated);

    return () => {
      console.log('Unsubscribing from notifications');
      subscription.unsubscribe();
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('notificationCreated', handleNotificationCreated);
    };
  }, [user?.id, fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    createNotification,
    refetchNotifications: fetchNotifications
  };
};
